import React from 'react';
import {
  useContent,
  useCaeEnforcement,
  useCaeMissingLogger,
  useConsumptionsAndEmissionsFromOneGraphWithLoading,
  type GfaServiceConfigLike,
} from '@oneaudi/feature-app-utils';
import { hasValidCaeContent } from '../utils/caeUtils';
import type { FalconContent, StagePropsFACE } from '../../types';
import { transformCmsDataToProps } from '../utils/transformCmsDataToProps';
import { Context } from '../Context';

interface CaeGateProps {
  readonly children: React.ReactNode;
}

const CaeGate: React.FC<CaeGateProps> = ({ children }) => {
  const rawContent = useContent<StagePropsFACE | FalconContent>() || undefined;
  const content = transformCmsDataToProps(rawContent as StagePropsFACE | FalconContent);

  const ctx = React.useContext(Context);
  const ids = (content?.consumptionAndEmissions ?? []).filter(Boolean);
  const gfaServiceConfigProvider: GfaServiceConfigLike = ctx.gfaServiceConfigProvider ?? {
    gfa: {},
  };

  const { caeDataRequired } = useCaeEnforcement({
    gfaServiceConfigProvider,
    acsResult: ctx.acsResult,
  });

  const { data: caeData, loading } = useConsumptionsAndEmissionsFromOneGraphWithLoading({
    ids,
    localeService: ctx.localeService as NonNullable<typeof ctx.localeService>,
    oneGraphService: ctx.oneGraphService as NonNullable<typeof ctx.oneGraphService>,
    logger: ctx.loggerService,
  });

  const hasCaeData = caeData.length > 0 && caeData.every((entry) => hasValidCaeContent(entry));

  const shouldHideForMissing = ids.length > 0 && caeDataRequired && (loading || !hasCaeData);

  const missingIds = shouldHideForMissing
    ? ids.filter((_, index) => !hasValidCaeContent(caeData[index]))
    : [];

  useCaeMissingLogger({
    enabled: ids.length > 0 && caeDataRequired && !loading && !hasCaeData,
    logger: ctx.loggerService,
    market: ctx.localeService?.countryCode ?? 'unknown',
    missingIds,
    pageUrl: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  if (shouldHideForMissing) {
    return null;
  }

  return children as React.ReactElement;
};

export default CaeGate;
